<template>
  <div class="auth-page">
    <main>
      <div v-if="clubId !== null" class="content content--side">
        <header class="codrops-header uk-flex uk-flex-center">
          <a class="header-wrapper" href="/login">
            <TenantLogo id="main-logo" logo-class="" />
          </a>
          <h1 class="uk-margin-remove uk-text-center">
            {{ $store.getters.clubNameShort }}
            <span>{{ $store.getters.appName }}</span>
          </h1>
        </header>
        <div v-if="registrationComplete" class="form registration-success">
          <h2><i class="bi bi-check-circle"></i> Registration complete</h2>
          <p>
            <strong>{{ registeredChildName }}</strong> has been registered with
            {{ $store.getters.clubNameShort }}.
          </p>

          <div class="install-next">
            <p class="install-next__title">
              <i class="bi bi-phone"></i> Next step — install the app
            </p>
            <p>
              Players use the mobile app. Install it from the App Store or
              Google Play, then log in with the email and password you just set.
            </p>
            <a class="form__link" href="/training-course.html#lesson-0">
              How to install the app →
            </a>
          </div>

          <div v-if="registeredChildren.length > 1" class="registered-children">
            <p class="registered-children__label">
              Registered under your login ({{ registeredChildren.length }}):
            </p>
            <ul>
              <li v-for="child in registeredChildren" :key="child">
                <i class="bi bi-person-check"></i> {{ child }}
              </li>
            </ul>
          </div>

          <p>
            Got another child at the club? Add them under this same login and
            switch between your children in the app — no need to sign in
            separately.
          </p>
          <div class="registration-success__actions">
            <button class="register-button" @click="startSiblingRegistration()">
              <i class="bi bi-person-plus"></i> Register another child
            </button>
            <router-link class="form__link" to="/login">
              {{ registeredChildren.length > 1 ? "All done — go to login" : "Go to login" }}
            </router-link>
          </div>
        </div>
        <div v-else class="form">
          <p class="form__intro">Please fill in the form below to get started. Fields marked with <span
              class="required-star">*</span> are required.</p>

          <!-- Multi-child hint: shown on the first registration so a parent
               knows up front they can add more than one child under one login. -->
          <div class="multi-child-hint" v-if="!isSiblingRegistration">
            <i class="bi bi-people"></i>
            <span>
              <strong>More than one child at the club?</strong> Register your
              first child now — you'll then be able to add each additional
              child under the <strong>same login</strong>, and switch between
              them in the app.
            </span>
          </div>

          <div class="sibling-banner" v-if="isSiblingRegistration">
            <i class="bi bi-people"></i>
            <span>
              Registering another child under your family login<span v-if="registeredChildren.length">
                (already added: <strong>{{ registeredChildren.join(", ") }}</strong>)</span>.
              <template v-if="familyPasswordLocked">
                Your email and password are locked so everyone shares one
                login — just enter the new child's details below.
              </template>
              <template v-else>
                Your email is locked to your family login — enter the
                <strong>same password</strong> you used before, then the new
                child's details.
              </template>
            </span>
          </div>

          <!-- Progress Indicator -->
          <div class="form__progress">
            <div class="progress-step" :class="{ active: currentStep >= 1, completed: isAccountInfoComplete }">
              <span class="step-number">1</span>
              <span class="step-label">Account</span>
            </div>
            <div class="progress-line" :class="{ active: isAccountInfoComplete }"></div>
            <div class="progress-step" :class="{ active: currentStep >= 2, completed: isPersonalInfoComplete }">
              <span class="step-number">2</span>
              <span class="step-label">Personal</span>
            </div>
            <div class="progress-line" :class="{ active: isPersonalInfoComplete }"></div>
            <div class="progress-step" :class="{ active: currentStep >= 3, completed: isFootballInfoComplete }">
              <span class="step-number">3</span>
              <span class="step-label">Football</span>
            </div>
            <div class="progress-line" :class="{ active: isFootballInfoComplete }"></div>
            <div class="progress-step" :class="{ active: currentStep >= 4 }">
              <span class="step-number">4</span>
              <span class="step-label">Consent</span>
            </div>
          </div>

          <!-- Section 1: Account Information -->
          <div class="form__section">
            <h3 class="form__section-title">
              <i class="bi bi-person-circle"></i> Account Information
            </h3>

            <div class="form__item">
              <label class="form__label" for="name">Player's Full Name <span class="required-star">*</span></label>
              <input :class="[
                'form__input',
                { 'danger-outline': validationValues.name.state === true },
                { 'success-outline': name && validationValues.name.state === false }
              ]" @change="checkChangeVlaidation(name, 'name')" @focus="currentStep = 1" type="text" v-model="name"
                id="name" placeholder="The child who plays, e.g. Sam Smith" autocomplete="off" />
              <p class="invalid-feedback" v-if="validationValues.name.state === true">
                {{ validationValues.name.text }}
              </p>
              <!-- Parents kept registering THEMSELVES here (their own name, the
                   child's DOB), so the squad list filled with "Renae" and
                   "andrew freestone". Spell it out. -->
              <p class="form__hint">
                Registering a child? Enter <strong>the player's</strong> name here —
                your own name goes in Parent/Guardian further down. One login
                covers all your children.
              </p>
            </div>

            <div class="form__item">
              <label class="form__label" for="email">
                Email Address <span class="required-star">*</span>
                <span v-if="isSiblingRegistration" class="locked-badge"><i class="bi bi-lock-fill"></i> family login</span>
              </label>
              <input :class="[
                'form__input',
                { 'danger-outline': validationValues.email.state === true },
                { 'success-outline': email && validationValues.email.state === false },
                { 'locked-input': isSiblingRegistration }
              ]" @change="checkChangeVlaidation(email, 'email')" @focus="currentStep = 1" type="email" v-model="email"
                id="email" placeholder="you@example.com" autocomplete="email" :readonly="isSiblingRegistration" />
              <p class="invalid-feedback" v-if="validationValues.email.state === true">
                {{ validationValues.email.text }}
              </p>
              <p class="form__hint email-exists-hint" v-if="emailAlreadyRegistered && !isSiblingRegistration">
                <i class="bi bi-info-circle"></i> This email already has a child
                registered. That's fine — keep it the same to add another child
                under one login, and use the <strong>same password</strong> as
                before.
              </p>
              <p class="form__hint" v-else>We'll use this email for account verification and communication.</p>
            </div>

            <!-- Password is hidden entirely once locked (sibling flow with the
                 family password already in memory) — nothing to type, and it
                 stays consistent across every child. -->
            <div class="form__item" v-if="!familyPasswordLocked">
              <label class="form__label" for="password">
                Password <span class="required-star">*</span>
              </label>
              <div class="form__input-wrap password-wrap">
                <input :class="[
                  'form__input',
                  { 'danger-outline': validationValues.password.state === true },
                  { 'success-outline': password.length >= 6 && validationValues.password.state === false }
                ]" @change="checkChangeVlaidation(password, 'password')" @focus="currentStep = 1"
                  :type="showPassword ? 'text' : 'password'" v-model="password" id="password"
                  :placeholder="isSiblingRegistration ? 'Enter the SAME password as your first child' : 'Minimum 6 characters'"
                  autocomplete="new-password" />
                <button type="button" class="password-toggle" @click="showPassword = !showPassword">
                  <i :class="showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'"></i>
                </button>
              </div>
              <p class="invalid-feedback" v-if="validationValues.password.state === true">
                {{ validationValues.password.text }}
              </p>
              <p class="form__hint" v-if="isSiblingRegistration">
                <i class="bi bi-info-circle"></i> Use the same password you set
                for your first child so they share one login.
              </p>
              <div class="password-requirements" v-if="password.length > 0">
                <span :class="{ met: password.length >= 6 }">
                  <i :class="password.length >= 6 ? 'bi bi-check-circle-fill' : 'bi bi-circle'"></i>
                  At least 6 characters
                </span>
              </div>
              <p class="form__password-strength" id="strength-output"></p>
            </div>
            <div class="form__item" v-else>
              <label class="form__label">
                Password
                <span class="locked-badge"><i class="bi bi-lock-fill"></i> family login</span>
              </label>
              <p class="form__hint">
                Using the same password as your other children — nothing to
                enter here.
              </p>
            </div>
          </div>

          <!-- Section 2: Personal Information -->
          <div class="form__section">
            <h3 class="form__section-title">
              <i class="bi bi-card-text"></i> Personal Information
            </h3>

            <div class="form__row">
              <div class="form__item uk-form dob">
                <label class="form__label" for="dob">Date of Birth <span class="required-star">*</span></label>
                <VueDatePicker :locale="'en-GB'" v-model="dob" name="dob" id="dob"
                  @input="checkChangeVlaidation(dob, 'dob')" @focus="currentStep = 2" format="dd/MM/yyyy"
                  :enable-time-picker="false" placeholder="Select your date of birth" />
                <p class="invalid-feedback" v-if="validationValues.dob.state === true">
                  {{ validationValues.dob.text }}
                </p>
              </div>

              <div class="form__item">
                <label class="form__label" for="phone">Phone Number</label>
                <input class="form__input" type="tel" v-model="phone" id="phone" @focus="currentStep = 2"
                  placeholder="07XXX XXXXXX" autocomplete="tel" />
              </div>
            </div>

            <div class="form__item uk-form address">
              <label class="form__label" for="address">Address</label>
              <textarea id="address" class="form__input form__textarea" v-model="address"
                placeholder="Enter your full address" rows="3" @focus="currentStep = 2"></textarea>
            </div>

            <div class="form__row">
              <div class="form__item uk-form post_code">
                <label class="form__label" for="post_code">Post Code</label>
                <input class="form__input" type="text" v-model="post_code" id="post_code" @focus="currentStep = 2"
                  placeholder="e.g. EH1 1AA" />
              </div>

              <div class="form__item uk-form place_of_birth">
                <label class="form__label" for="place_of_birth">Place of Birth</label>
                <input class="form__input" type="text" v-model="place_of_birth" id="place_of_birth"
                  @focus="currentStep = 2" placeholder="e.g. Edinburgh" />
              </div>
            </div>

            <div class="form__row">
              <div class="form__item uk-form">
                <label class="form__label" for="country_of_birth">Country of Birth</label>
                <input class="form__input" type="text" v-model="country_of_birth" id="country_of_birth"
                  @focus="currentStep = 2" placeholder="e.g. Scotland" />
              </div>

              <div class="form__item uk-form nationality">
                <label class="form__label" for="nationality">Nationality</label>
                <input class="form__input" type="text" v-model="nationality" id="nationality" @focus="currentStep = 2"
                  placeholder="e.g. British" />
              </div>
            </div>

            <div class="form__row">
              <div class="form__item">
                <label class="form__label" for="parent_name">Parent/Guardian Name</label>
                <input class="form__input" type="text" v-model="parent_name" id="parent_name" @focus="currentStep = 2"
                  placeholder="Your name (for players under 18)" />
                <p class="invalid-feedback" v-if="parentIsPlayer">
                  The player's name and the parent's name are the same — the
                  Player's Full Name at the top should be the child who plays.
                </p>
              </div>
            </div>

            <div class="form__row">
              <div class="form__item">
                <label class="form__label" for="emergency_contact_one">Emergency Contact 1</label>
                <input class="form__input" type="tel" v-model="emergency_contact_one" id="emergency_contact_one"
                  @focus="currentStep = 2" placeholder="Name and phone number" />
              </div>

              <div class="form__item">
                <label class="form__label" for="emergency_contact_two">Emergency Contact 2</label>
                <input class="form__input" type="tel" v-model="emergency_contact_two" id="emergency_contact_two"
                  @focus="currentStep = 2" placeholder="Name and phone number" />
              </div>
            </div>
          </div>

          <!-- Section 3: Football Information -->
          <div class="form__section">
            <h3 class="form__section-title">
              <i class="bi bi-dribbble"></i> Football Information
            </h3>

            <div class="form__row">
              <div class="form__item">
                <label class="form__label" for="teamSelect">Select Team</label>
                <select v-model="teamId" id="teamSelect" class="form__select" @focus="currentStep = 3">
                  <option disabled value="">Please select a team</option>
                  <option v-for="team in teamData" :value="team.teamId" :key="team.teamId">
                    {{ team.teamName }}
                  </option>
                </select>
              </div>

              <div class="form__item">
                <label class="form__label" for="coachSelect">Assigned Coach</label>
                <select v-model="coachId" id="coachSelect" class="form__select" @focus="currentStep = 3">
                  <option disabled value="">Please select a coach</option>
                  <option v-for="coach in coachData" :value="coach.id" :key="coach.id">
                    {{ coach.name }}
                  </option>
                </select>
              </div>
            </div>

            <div class="form__row">
              <div class="form__item">
                <label class="form__label" for="position">Primary Position</label>
                <select id="position" v-model="position" class="form__select" @focus="currentStep = 3">
                  <option disabled value="">Select your main position</option>
                  <option value="Goalkeeper">Goalkeeper</option>
                  <option value="Right Full-back (Wingback)">Right Full-back (Wingback)</option>
                  <option value="Left Full-back (Wingback)">Left Full-back (Wingback)</option>
                  <option value="Center-back left">Center-back (Left)</option>
                  <option value="Center-back right">Center-back (Right)</option>
                  <option value="Defensive Midfielder">Defensive Midfielder</option>
                  <option value="Centre midfield">Central Midfielder</option>
                  <option value="Centre Defensive Midfield">Central Defensive Midfielder</option>
                  <option value="Right Midfielder (Winger)">Right Winger</option>
                  <option value="Left Midfielder (Winger)">Left Winger</option>
                  <option value="Attacking Midfielder">Attacking Midfielder</option>
                  <option value="Center Forward (Striker) left">Striker (Left)</option>
                  <option value="Center Forward (Striker) right">Striker (Right)</option>
                </select>
              </div>

              <div class="form__item">
                <label class="form__label" for="preferred_foot">Preferred Foot</label>
                <select id="preferred_foot" v-model="preferred_foot" class="form__select" @focus="currentStep = 3">
                  <option disabled value="">Select preferred foot</option>
                  <option value="0">Right</option>
                  <option value="1">Left</option>
                </select>
              </div>
            </div>

            <div class="form__row">
              <div class="form__item uk-form">
                <label class="form__label" for="previous_club">Previous Club</label>
                <input class="form__input" type="text" v-model="previous_club" id="previous_club"
                  @focus="currentStep = 3" placeholder="Your last club (if any)" />
              </div>

              <div class="form__item uk-form dob">
                <label class="form__label" for="joined_date">Date Joined Club</label>
                <VueDatePicker :locale="'en-GB'" v-model="joined_date" name="joined_date" id="joined_date"
                  :enable-time-picker="false" format="dd/MM/yyyy" @focus="currentStep = 3"
                  placeholder="When did you join?" />
              </div>
            </div>

            <div class="form__item" @focusin="currentStep = 3">
              <label class="form__label">Medical Information</label>
              <MedicalConditionsEditor v-model="medicalInfo" />
            </div>
          </div>

          <!-- Section 4: Consent & Agreements -->
          <div class="form__section">
            <h3 class="form__section-title consent-select-all">
              <label class="consent-check">
                <input type="checkbox" id="checkbox-select-all-consent" :checked="allConsent"
                  @change="toggleAllConsent($event.target.checked)" />
                <span><i class="bi bi-shield-check"></i> Consent & Agreements <small class="select-all-hint">(select all)</small></span>
              </label>
            </h3>

            <div class="form__item consent-item">
              <label class="consent-check">
                <input type="checkbox" id="checkbox-photo" :checked="permission === 1"
                  @change="permission = $event.target.checked ? 1 : 0" @focus="currentStep = 4" />
                <span class="consent-text">
                  <strong>Photo Permission</strong><br />
                  I give permission to take and use photographs/videos for club promotional purposes
                </span>
              </label>
            </div>

            <div class="form__item consent-item gdpr-consent">
              <label class="consent-check" :class="{ 'is-invalid': gdprConsentError }">
                <input type="checkbox" id="checkbox-gdpr" :checked="gdprConsent"
                  @change="gdprConsent = $event.target.checked; gdprConsentError = false" @focus="currentStep = 4" />
                <span class="consent-text">
                  <strong>Privacy Policy & Data Protection <span class="required-star">*</span></strong><br />
                  I have read and agree to the <a href="/privacy-policy" target="_blank" class="consent-link">Privacy
                    Policy</a>.
                  I understand that my personal data will be processed in accordance with {{ legal.privacyLawShort }}
                  and used only for the purposes of managing my membership and football activities.
                </span>
              </label>
              <p class="invalid-feedback" v-if="gdprConsentError">
                You must accept the Privacy Policy and data protection terms to register.
              </p>
            </div>

            <div class="form__item consent-item">
              <label class="consent-check" :class="{ 'is-invalid': termsConsentError }">
                <input type="checkbox" id="checkbox-terms" :checked="termsConsent"
                  @change="termsConsent = $event.target.checked; termsConsentError = false" @focus="currentStep = 4" />
                <span class="consent-text">
                  <strong>Terms & Conditions <span class="required-star">*</span></strong><br />
                  I agree to the <a href="/terms" target="_blank" class="consent-link">Terms and Conditions</a>
                  of membership and understand my responsibilities as a member.
                </span>
              </label>
              <p class="invalid-feedback" v-if="termsConsentError">
                You must accept the Terms and Conditions to register.
              </p>
            </div>

            <div class="form__item consent-item">
              <label class="consent-check" :class="{ 'is-invalid': videoFootageConsentError }">
                <input type="checkbox" id="checkbox-video-footage" :checked="videoFootageConsent"
                  @change="videoFootageConsent = $event.target.checked; videoFootageConsentError = false" @focus="currentStep = 4" />
                <span class="consent-text">
                  <strong>Video Footage Confidentiality <span class="required-star">*</span></strong><br />
                  I acknowledge that all video footage accessed via this app is confidential and provided solely for
                  {{ $store.getters.clubNameShort }} Academy performance analysis purposes. I understand it may contain
                  personal data and images of children and young people and must be handled in line with safeguarding
                  responsibilities and {{ legal.privacyLawShort }}. I will not download, share, or distribute any footage with unauthorised
                  persons. Any breach may result in restricted access and disciplinary action in accordance with club
                  policies.
                </span>
              </label>
              <p class="invalid-feedback" v-if="videoFootageConsentError">
                You must acknowledge the video footage confidentiality terms to register.
              </p>
            </div>
          </div>

          <!--<ImageUploader
            @populateImageObjectFromUpload="imageUploadFileDataChnged($event)"
            @populateImageFileDataFromUpload="
              imageUploadObjectDataChanged($event)
            "
            @clearImageDataFromUpload="clearImageDataFromUpload()"
            :imageURL="userImage"
          />-->

          <div class="form__item" v-if="feePlans.length > 0 && !fromTrialOffer">
            <label class="form__label" for="feePlanSelect">Membership fee</label>
            <select id="feePlanSelect" v-model="selectedFeePlanId" class="form__input">
              <option :value="null">Arrange payment with the club later</option>
              <option v-for="plan in feePlans" :key="plan.id" :value="plan.id">
                {{ plan.name }} — {{ formatPlanPrice(plan) }}
              </option>
            </select>
            <p class="form__submit-hint" v-if="selectedFeePlanId">
              <i class="bi bi-info-circle"></i> After registering you'll be taken
              to a secure Stripe page to set up this payment.
            </p>
          </div>

          <div class="form__item form__item--actions">
            <div class="login-link-wrapper">
              <span>Already have an account?</span>
              <router-link class="form__link" to="/login">Login here</router-link>
            </div>
            <button-spinner ref="loadingButton" @click="register()" :disabled="!canSubmit" class="register-button">
              <i class="bi bi-check-circle"></i> Complete Registration
            </button-spinner>
            <p class="form__submit-hint" v-if="!canSubmit">
              <i class="bi bi-info-circle"></i> Please complete all required fields and accept the terms to register.
            </p>
          </div>
        </div>
      </div>
      <div v-else class="content content--side">
        <h1>Looks like your URL is wrong. Check it and try again.</h1>
      </div>
    </main>
  </div>
</template>

<script>
//import ImageUploader from "../../../components/ImageUploader";
import VueDatePicker from "@vuepic/vue-datepicker";
import "@vuepic/vue-datepicker/dist/main.css";
import img from "./img/register.jpg";
import { AUTH_SIGNUP } from "@/store/actions/auth";
import { StripeConnect } from "@/services/stripe-connect-service.js";
import { isFeatureEnabled, getPaymentsCurrency, getLegalConfig } from "@/config/tenant";
import { USER_POPULATE } from "@/store/actions/user.js";
import { Users } from "@/services/user-service.js";
import { Trials } from "@/services/trials-service.js";
import { Teams } from "@/services/teams-service.js";
import TenantLogo from "@/components/TenantLogo/index.vue";
import MedicalConditionsEditor from "@/components/MedicalInfo/MedicalConditionsEditor.vue";
import moment from "moment";
//import zxcvbn from "./zxcvbn.js";
export default {
  components: {
    VueDatePicker,
    TenantLogo,
    MedicalConditionsEditor,
  },
  data() {
    return {
      needsValidation: [],
      validationMessage: null,
      validationValues: {
        name: {
          state: false,
          text: "Please add a name please!",
        },
        email: {
          state: false,
          text: "Please add an email address",
        },
        password: {
          state: false,
          text: "Please add password please!",
        },
        dob: {
          state: false,
          text: "Please add a date of birth please!",
        },
      },
      coachData: [],
      teamData: [],
      img: img,
      name: "",
      email: "",
      password: "",
      dob: "",
      post_code: "",
      address: "",
      place_of_birth: "",
      country_of_birth: "",
      nationality: "",
      previous_club: "",
      position: "",
      phone: "",
      emergency_contact_one: "",
      emergency_contact_two: "",
      userType: "User",
      action: "register",
      teamId: 0,
      permission: 0,
      clubId: null,
      coachId: 0,
      team_module: 1,
      preferred_foot: 0,
      joined_date: "",
      medical: "",
      // Structured medical declaration (Yes/No + one entry per condition).
      // `medical` above now only carries the free-text "anything else" notes.
      medicalInfo: { hasConditions: null, conditions: [], notes: "" },
      parent_name: "",
      // UX improvements
      showPassword: false,
      currentStep: 1,
      // GDPR and consent
      gdprConsent: false,
      termsConsent: false,
      videoFootageConsent: false,
      feePlans: [],
      selectedFeePlanId: null,
      // True when the family arrived from a trial offer AND the club takes the
      // trial deposit in the app: that deposit is their payment step, so the
      // season-fee selector is hidden rather than sending them to Stripe twice.
      fromTrialOffer: false,
      registrationComplete: false,
      registeredChildName: "",
      isSiblingRegistration: false,
      // True only when the family password is carried in memory from the
      // in-session "Register another child" button — NOT when a parent is
      // typing it fresh after a page reload (else the field would vanish
      // mid-type once it hit 6 chars). Drives whether the password field
      // is hidden.
      familyPasswordFromMemory: false,
      // Non-blocking hint: this email already has a child registered, so the
      // parent is (probably) adding a sibling — fine, not an error.
      emailAlreadyRegistered: false,
      // Names of children registered under this family login this session,
      // for the running list on the form and success screen.
      registeredChildren: [],
      gdprConsentError: false,
      termsConsentError: false,
      videoFootageConsentError: false,
      allConsent: false,
      togglingAll: false,
      image: {
        imageName: "",
        path: "",
        file: null,
      },
      imageFile: null,
      imageURL: "",
      userImage: "",
    };
  },
  computed: {
    legal() {
      return getLegalConfig();
    },
    // Same text in "Player's Full Name" and "Parent/Guardian Name" = a parent
    // registering themselves instead of their child.
    parentIsPlayer() {
      const norm = (s) => String(s || "").toLowerCase().replace(/[^a-z]/g, "");
      const a = norm(this.name);
      return a.length > 2 && a === norm(this.parent_name);
    },
    // The family password field is hidden only when we still hold it in
    // memory from the first child (in-session sibling flow). On a fresh
    // page load the password isn't persisted, so the parent must re-enter
    // it — the field stays visible with the "same password" guidance.
    familyPasswordLocked() {
      return (
        this.isSiblingRegistration &&
        this.familyPasswordFromMemory &&
        this.password.length >= 6
      );
    },
    isAccountInfoComplete() {
      return this.name && this.email && this.password.length >= 6 &&
        !this.validationValues.name.state &&
        !this.validationValues.email.state &&
        !this.validationValues.password.state;
    },
    isPersonalInfoComplete() {
      return this.dob && !this.validationValues.dob.state;
    },
    isFootballInfoComplete() {
      return this.teamId && this.coachId && this.position;
    },
    canSubmit() {
      return this.isAccountInfoComplete &&
        this.isPersonalInfoComplete &&
        this.gdprConsent &&
        this.termsConsent &&
        this.videoFootageConsent;
    },
  },
  async created() {
    if ((await this.checkTeamIdexists()) === true) {
      this.clubId = this.$route.params.clubId;
      this.coachData = await this.populateCoachesData();
      this.coachId = this.coachData[0].id;
      this.teamData = await this.populateTeamData();
      this.teamId = this.teamData[0].teamId;
      this.loadFeePlans();
      this.checkTrialOffer();
      if (this.$route.query.sibling === "1") {
        this.applyFamilyPrefill();
      }
    }
  },
  watch: {
    gdprConsent() { if (!this.togglingAll) this.updateAllConsent(); },
    termsConsent() { if (!this.togglingAll) this.updateAllConsent(); },
    videoFootageConsent() { if (!this.togglingAll) this.updateAllConsent(); },
    permission() { if (!this.togglingAll) this.updateAllConsent(); },
  },
  methods: {
    // Family (sibling) registration: siblings share the parent's email, which
    // the login flow already turns into a child-switcher (multiple users rows
    // per email -> userHasMoreThanOneAccount). Family-level fields are kept
    // so a parent doesn't re-type them per child; the password is never
    // persisted - in-page flows keep it in memory, the Stripe-return flow
    // asks the parent to re-enter it.
    familyPrefillKey() {
      return "grassrootsFamilyPrefill";
    },
    saveFamilyPrefill() {
      try {
        sessionStorage.setItem(
          this.familyPrefillKey(),
          JSON.stringify({
            clubId: this.clubId,
            email: this.email,
            phone: this.phone,
            address: this.address,
            post_code: this.post_code,
            parent_name: this.parent_name,
            emergency_contact_one: this.emergency_contact_one,
            emergency_contact_two: this.emergency_contact_two,
            nationality: this.nationality,
            country_of_birth: this.country_of_birth,
            children: this.registeredChildren,
          }),
        );
      } catch (error) {
        // Storage unavailable (private mode etc.) - sibling prefill just won't work
      }
    },
    applyFamilyPrefill() {
      try {
        const raw = sessionStorage.getItem(this.familyPrefillKey());
        if (!raw) return;
        const prefill = JSON.parse(raw);
        if (prefill.clubId !== this.clubId) return;
        this.email = prefill.email || "";
        this.phone = prefill.phone || "";
        this.address = prefill.address || "";
        this.post_code = prefill.post_code || "";
        this.parent_name = prefill.parent_name || "";
        this.emergency_contact_one = prefill.emergency_contact_one || "";
        this.emergency_contact_two = prefill.emergency_contact_two || "";
        this.nationality = prefill.nationality || "";
        this.country_of_birth = prefill.country_of_birth || "";
        this.registeredChildren = Array.isArray(prefill.children)
          ? prefill.children
          : [];
        this.isSiblingRegistration = true;
      } catch (error) {
        // Bad/missing prefill - fall through to a blank form
      }
    },
    startSiblingRegistration() {
      // Keep family fields (and the in-memory password); clear the
      // child-specific ones and per-child consents
      this.name = "";
      this.dob = "";
      this.position = "";
      this.preferred_foot = "";
      this.medical = "";
      this.medicalInfo = { hasConditions: null, conditions: [], notes: "" };
      this.place_of_birth = "";
      this.previous_club = "";
      this.selectedFeePlanId = null;
      this.togglingAll = true;
      this.gdprConsent = false;
      this.termsConsent = false;
      this.videoFootageConsent = false;
      this.allConsent = false;
      this.permission = 0;
      this.togglingAll = false;
      this.isSiblingRegistration = true;
      // In-session flow: the password is still in memory, so lock/hide it.
      this.familyPasswordFromMemory = this.password.length >= 6;
      this.registrationComplete = false;
      this.currentStep = 1;
      window.scrollTo(0, 0);
    },
    async checkTrialOffer() {
      if (!this.$route.query.trial || !isFeatureEnabled("trialRegistration")) return;
      try {
        const res = await Trials.getPublicConfig(this.clubId);
        this.fromTrialOffer = !!res.data?.config?.deposit?.enabled;
      } catch (e) {
        this.fromTrialOffer = false;
      }
    },
    async loadFeePlans() {
      // Fees at registration are a grassroots-tier feature (registrationFees
      // flag): Pro clubs run Club Payments for other activities, but their
      // players don't pay to register. Any failure (feature disabled, no
      // plans, endpoint missing) simply means no fee selector is shown -
      // registration must never break on this.
      if (!isFeatureEnabled("registrationFees")) {
        this.feePlans = [];
        return;
      }
      try {
        const response = await StripeConnect.listFeePlans(this.clubId);
        this.feePlans = response.data.data || [];
      } catch (error) {
        this.feePlans = [];
      }
    },
    formatPlanPrice(plan) {
      const money = (pence) =>
        new Intl.NumberFormat("en-GB", {
          style: "currency",
          currency: getPaymentsCurrency(),
        }).format((pence || 0) / 100);
      if (plan.plan_type === "installments") {
        const stages = (plan.installments || [])
          .map((inst) => inst.label)
          .join(" + ");
        return `${money(plan.amount_pence)} for the season${stages ? ` (${stages})` : ""}`;
      }
      if (plan.billing_interval === "month") return `${money(plan.amount_pence)} / month`;
      if (plan.billing_interval === "year") return `${money(plan.amount_pence)} / year`;
      return `${money(plan.amount_pence)} one-off`;
    },
    toggleAllConsent(checked) {
      const val = !!checked;
      this.togglingAll = true;
      this.permission = val ? 1 : 0;
      this.gdprConsent = val;
      this.termsConsent = val;
      this.videoFootageConsent = val;
      this.allConsent = val;
      if (val) {
        this.gdprConsentError = false;
        this.termsConsentError = false;
        this.videoFootageConsentError = false;
      }
      this.$nextTick(() => { this.togglingAll = false; });
    },
    updateAllConsent() {
      this.allConsent = !!(this.permission && this.gdprConsent && this.termsConsent && this.videoFootageConsent);
    },
    clearImageDataFromUpload() {
      ((this.imageURL = ""),
        (this.image = {
          imageName: "",
          path: "",
          file: null,
        }));
    },
    imageUploadObjectDataChanged(data) {
      this.image = data;
    },
    imageUploadFileDataChnged(data) {
      this.imageFile = data;
    },
    async populateTeamData() {
      const teamData = {
        clubId: this.clubId,
        action: "populateTeamData",
      };

      const responce = await Teams.find(teamData).catch((error) => {
        console.log(error);
      });

      return responce.data.length > 0 ? responce.data : [];
    },
    async populateCoachesData() {
      const coachesData = {
        clubId: this.clubId,
        action: "populateCoachesData",
      };

      const responce = await Users.find(coachesData).catch((error) => {
        console.log(error);
      });

      return responce.data.length > 0 ? responce.data : [];
    },
    async checkTeamIdexists() {
      const userData = {
        clubId: this.$route.params.clubId,
        action: "checkTeamIdexists",
      };
      const responce = await Users.find(userData).catch((error) => {
        console.log(error);
      });

      return responce.data.length > 0 ? true : false;
    },
    async getTeamsById(clientId) {
      const userData = {
        clientId: clientId,
        action: "getUserByID",
      };
      const responce = await Users.find(userData).catch((error) => {
        console.log(error);
      });
      if (responce.data.length > 0) {
        this.userData = responce.data[0];
        const userData = responce.data[0];
        this.$store.dispatch(USER_POPULATE, { userData });
        if (
          this.userData.userType === "Admin" ||
          this.userData.userType === "Coach"
        ) {
          this.getAllUsersForThisAdmin();
        }
      }
    },
    async checkIfEmailExists(value) {
      // Multi-child is a supported flow: one parent registers every child
      // under the SAME email. So an existing email is NOT an error — it just
      // means this parent already has a child registered. We surface a
      // friendly hint but NEVER block submit on it. In sibling mode the
      // email is the locked family login, so skip the check entirely.
      if (this.isSiblingRegistration) {
        this.emailAlreadyRegistered = false;
        return;
      }
      const userData = {
        email: value,
        action: "checkEmailexists",
      };
      const responce = await Users.find(userData).catch((error) => {
        console.log(error);
      });
      this.emailAlreadyRegistered = !!(
        responce &&
        responce.data &&
        responce.data.length > 0
      );
    },
    checkChangeVlaidation(value, elementName) {
      if (elementName === "email") {
        if (value !== "") {
          this.checkIfEmailExists(value);
        } else {
          this.validationValues.email.state = false;
          this.validationValues.email.text = "Please add an email address";
        }
        const validated = this.requiredEmailValidation(value);
        this.validationValues[elementName].state = validated !== true;
      } else {
        JSON.stringify(value).length > 0
          ? (this.validationValues[elementName].state = false)
          : (this.validationValues[elementName].state = true);
      }
    },
    setValidationMessage() {
      if (this.needsValidation.length === 0) {
        this.validationMessage = null;
        this.needsValidation = [];
      } else {
        this.validationMessage =
          "Please fix the validation errors before you can proceed.";
        this.isLoading = false;
      }
    },
    reCheckValidation() {
      let validation = this.needsValidation.length === 0;
      validation = this.requiredEmailValidation(this.email);
      this.setValidationMessage();
      return validation;
    },
    populateValidationArray(validationData) {
      return validationData.map((validation) => {
        return validation === true;
      });
    },
    filterValidationResults(validationResults) {
      return validationResults.filter((result) => {
        return result === true;
      });
    },
    togglevalaidationErrors(name, validationState) {
      this.validationValues[name].state = validationState;
    },
    requiredValidation(value) {
      return !value || value.length === 0;
    },
    requiredEmailValidation(value) {
      var validRegex =
        /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9-]+(?:\.[a-zA-Z0-9-]+)*$/;
      return validRegex.test(value);
    },
    setUpValidation(validationValue, validationName) {
      let validationstate = false;
      if (validationName === "email") {
        validationstate = !this.requiredEmailValidation(validationValue);
      } else {
        validationstate = this.requiredValidation(validationValue);
      }
      this.togglevalaidationErrors(validationName, validationstate);
      return validationstate;
    },
    validationCheck() {
      let validationData = [];
      this.needsValidation = [];
      validationData.push(this.setUpValidation(this.name, "name"));
      validationData.push(this.setUpValidation(this.email, "email"));
      validationData.push(this.setUpValidation(this.password, "password"));
      validationData.push(this.setUpValidation(this.dob, "dob"));
      let validationResults = this.populateValidationArray(validationData);
      this.needsValidation = this.filterValidationResults(validationResults);
      return this.reCheckValidation();
    },
    medicalDeclarationError() {
      const m = this.medicalInfo || {};
      if (m.hasConditions !== 1 && m.hasConditions !== 0) {
        return "Please answer the medical information question (Yes or No).";
      }
      if (m.hasConditions === 1) {
        const incomplete = (m.conditions || []).some(
          (c) => !(c.condition_name && c.condition_name.trim()) && !(c.medication && c.medication.trim()),
        );
        if (incomplete) {
          return "Please name each medical condition (or remove the empty one).";
        }
      }
      return null;
    },
    register() {
      // A parent who typed their own name as the player (same name in both
      // boxes) is about to create an account for themselves, not the child.
      if (this.parentIsPlayer) {
        this.currentStep = 1;
        this.$snack.danger({ text: "Player's Full Name should be the child who plays — it currently matches the Parent/Guardian name." });
        return;
      }
      // Medical declaration is mandatory: a Yes/No answer, and a name for
      // every condition listed.
      const medicalError = this.medicalDeclarationError();
      if (medicalError) {
        this.currentStep = 3;
        this.$snack.danger({ text: medicalError });
        return;
      }
      // Check GDPR consent
      if (!this.gdprConsent) {
        this.gdprConsentError = true;
        this.$snack.danger({ text: 'Please accept the Privacy Policy and data protection terms to register.' });
        return;
      }
      if (!this.termsConsent) {
        this.termsConsentError = true;
        this.$snack.danger({ text: 'Please accept the Terms and Conditions to register.' });
        return;
      }
      if (!this.videoFootageConsent) {
        this.videoFootageConsentError = true;
        this.$snack.danger({ text: 'Please acknowledge the video footage confidentiality terms to register.' });
        return;
      }

      if (this.validationCheck() === true) {
        if (this.password.length > 5) {
          this.$refs.loadingButton.startLoading();
          const dob = this.formatDatesForSQL(this.dob);
          const joined_date = this.formatDatesForSQL(this.joined_date);
          const age_group = this.formatAgeGroupForSQL(dob);
          const {
            name,
            email,
            password,
            position,
            phone,
            userType,
            action,
            coachId,
            teamId,
            clubId,
            emergency_contact_one,
            emergency_contact_two,
            team_module,
            permission,
            medical,
            preferred_foot,
            place_of_birth,
            country_of_birth,
            nationality,
            previous_club,
            post_code,
            address,
            parent_name,
          } = this;

          if (this.image.imageFile !== null && this.imageFile !== null) {
            this.fileName = this.imageFile.imageName;
            this.imageFile = this.image;
          }

          this.$store
            .dispatch(AUTH_SIGNUP, {
              name,
              email,
              password,
              dob,
              position,
              phone,
              userType,
              action,
              imageFile: this.imageFile,
              fileName: this.fileName,
              coachId,
              teamId,
              clubId,
              age_group,
              emergency_contact_one,
              emergency_contact_two,
              team_module,
              permission,
              joined_date,
              medical: this.medicalInfo.notes || medical,
              has_medical_conditions: this.medicalInfo.hasConditions,
              medical_conditions:
                this.medicalInfo.hasConditions === 1 ? this.medicalInfo.conditions : [],
              preferred_foot,
              place_of_birth,
              country_of_birth,
              nationality,
              previous_club,
              post_code,
              address,
              parent_name,
              // Present when the family arrived from a trials invite link;
              // lets the backend stamp that trial registration as converted.
              trial: this.$route.query.trial || null,
              trialToken: this.$route.query.t || null,
              gdpr_consent: this.gdprConsent ? 1 : 0,
              gdpr_consent_date: new Date().toISOString(),
              video_footage_consent: this.videoFootageConsent ? 1 : 0,
              video_footage_consent_date: new Date().toISOString(),
            })
            .then(async () => {
              this.$refs.loadingButton.stopLoading();
              this.registeredChildName = this.name;
              if (!this.registeredChildren.includes(this.name)) {
                this.registeredChildren.push(this.name);
              }
              // Family details survive for "Register another child" - both
              // in-page and after the round-trip to Stripe Checkout
              this.saveFamilyPrefill();

              // If a membership fee plan was chosen, hand over to Stripe
              // Checkout; the user account already exists either way.
              if (this.selectedFeePlanId) {
                try {
                  const plan = this.feePlans.find(
                    (p) => p.id === this.selectedFeePlanId,
                  );
                  if (plan && plan.plan_type === "installments") {
                    // Season fees: create the member's season account and
                    // send them straight to the first stage (the deposit).
                    const statusRes =
                      await StripeConnect.getPublicInstallmentStatus(
                        this.clubId,
                        this.email,
                        this.selectedFeePlanId,
                      );
                    const stages =
                      statusRes.data.data?.ledger?.installments || [];
                    const firstDue = stages.find(
                      (s) => s.outstanding_pence > 0,
                    );
                    if (firstDue) {
                      const checkout =
                        await StripeConnect.createInstallmentCheckoutByEmail(
                          this.clubId,
                          this.email,
                          this.selectedFeePlanId,
                          firstDue.id,
                        );
                      window.location.href = checkout.data.data.url;
                      return;
                    }
                  } else {
                    const response = await StripeConnect.createFeeCheckout({
                      clubId: this.clubId,
                      feePlanId: this.selectedFeePlanId,
                      email: this.email,
                    });
                    window.location.href = response.data.data.url;
                    return;
                  }
                } catch (error) {
                  this.$snack.danger({
                    text: "Registration complete, but the payment page could not be opened. The club will be in touch about payment.",
                  });
                }
              }
              this.registrationComplete = true;
              window.scrollTo(0, 0);
            })
            .catch((error) => {
              this.$refs.loadingButton.stopLoading();
              // A 409 from the backend carries a message meant for the
              // family (duplicate account / invite already used).
              const backendMessage = error?.response?.data?.error;
              this.$snack.danger({
                text: backendMessage || error.message,
              });
            });
        }
      }
    },
    formatAgeGroupForSQL(dob) {
      const d = new Date(dob);
      return d.getFullYear();
    },
    formatDatesForSQL(date) {
      return moment(date).format(moment.HTML5_FMT.DATE);
    },
    createObjectURLCheck(file, imageFileName, imageNumber) {
      if (file.type === "image/heic") {
        this.convertHeicToJpg(file, imageFileName, imageNumber);
      }
      "srcObject" in this
        ? (this.srcObject = file)
        : (this.image.path = URL.createObjectURL(file));
    },
    imageChange(event, imageFileName, imageNumber) {
      const file = event.currentTarget.files[0];
      this.createObjectURLCheck(file, imageFileName, imageNumber);
      this.image.file = file;
      this.image.imageName = file.name;

      var files = event.target.files || event.dataTransfer.files;
      if (!files.length) return;
      this.createImage(files[0]);
    },
    createImage(file) {
      //var image = new Image();
      var reader = new FileReader();
      //var vm = this;

      reader.readAsDataURL(file);
      reader.onload = (event) => {
        this.imageFile = event.target.result;
      };
    },
    getPath(p, imageNumber) {
      if (p === undefined) {
        return;
      }
      return this.checkTheimageForBlob(p, imageNumber);
    },
    checkforDotAtTheStartOfUrl(p) {
      return p.startsWith(".") ? p.substring(1) : p;
    },
    checkTheimageForBlob(p, imageNumber) {
      if (p !== null) {
        var URL = this.checkforDotAtTheStartOfUrl(p);
      }
      switch (typeof URL) {
        case "string":
          URL.search("blob:") !== -1
            ? p
            : `${window.location.protocol}//${window.location.host}${URL}`;
          return URL.search("blob:") !== -1
            ? p
            : `${window.location.protocol}//${window.location.host}${URL}`;
        case "object":
          return this[imageNumber].path;
      }
    },
    generateOrgId(teamName) {
      const firstPart = teamName.replace(/\s+/g, "-").toLowerCase();
      const lastPart = Math.random().toString(36).substr(2, 9);
      return firstPart + lastPart;
    },
  },
  mounted() {
    /*const passwordInput = document.querySelector("#password");
    const passwordFeedback = document.querySelector("#strength-output");
    const strengthStr = {
      0: "Worst",
      1: "Bad",
      2: "Weak",
      3: "Good",
      4: "Strong",
    };

    if (passwordInput !== null) {
      passwordInput.addEventListener("input", () => {
        const val = passwordInput.value;
        const result = zxcvbn(val);
        // We want to reveal the image as the password gets stronger. Since the zxcvbn.score has
        // only 5 different values (0-4) we will use the zxcvbn.guesses_log10 output.
        // The guesses_log10 will be >= 11 when the password is considered strong,
        // so we want to map a factor of 1 (all pixelated) to 100 (clear image) to
        // a value of 0 to 11 of guesses_log10.
        // This result will be used in the render function.
        // pxFactor = (99 / 11) * Math.min(11, Math.round(result.guesses_log10)) + 1;

        // so we see most of the time pixels rather than approaching a clear image sooner..
        //if (pxFactor != 1 && pxFactor != 100) {
        //  pxFactor -= (pxFactor / 100) * 50;
        // }

        passwordFeedback.innerHTML =
          val !== "" ? `Password strength: ${strengthStr[result.score]}` : "";
        //render();
      });
    }*/
  },
};
</script>

<style lang="scss">
@import "../styles/auth-styles.scss";
</style>

<style lang="scss" scoped>
/* This long registration form fights auth-styles.scss (built for the compact
   login card): its .form__item is a 1fr/2fr label-beside-input grid with no
   page max-width, which sprawls badly on wide screens. Constrain the page
   and stack labels above inputs instead. */
.content--side {
  max-width: 920px;
  margin: 0 auto;
  width: 100%;
}

.form :deep(.form__item) {
  display: block;
}

.form :deep(.form__label) {
  display: block;
  margin-bottom: 0.4rem;
}

/* Kill the global 4rem offset on .form__item.uk-form.dob (a fix for another
   page) and make the datepicker fill its grid cell like other inputs. */
.form :deep(.form__item.uk-form.dob) {
  margin-top: 0 !important;
}

.form :deep(.dp__main) {
  width: 100%;
}

.form__textarea {
  width: 100%;
  border-radius: 8px;
  resize: vertical;
  min-height: 90px;
  font-weight: normal;
}

/* Native consent checkboxes (bootstrap-vue's b-form-checkbox change events
   are unreliable under the Vue 3 compat build — the select-all never fired) */
.consent-check {
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  cursor: pointer;
  margin: 0;
  font-weight: normal;

  input[type="checkbox"] {
    width: 18px;
    height: 18px;
    margin-top: 0.15rem;
    flex: none;
    accent-color: #28a745;
    cursor: pointer;
  }

  &.is-invalid .consent-text strong {
    color: #dc3545;
  }
}

.registration-success {
  text-align: center;
  padding: 2rem 1rem;

  h2 {
    color: #1e7b3c;

    i {
      margin-right: 0.4rem;
    }
  }

  p {
    color: #555;
    max-width: 420px;
    margin: 0.75rem auto;
  }

  .registration-success__actions {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    margin-top: 1.5rem;
  }

  .install-next {
    background: #e8f2fd;
    border: 1px solid #b6d7f5;
    border-radius: 8px;
    padding: 1rem 1.25rem;
    margin: 1.25rem auto;
    max-width: 420px;

    .install-next__title {
      font-weight: 700;
      color: #1a4f82;
      margin: 0 0 0.25rem;
    }

    a {
      display: inline-block;
      margin-top: 0.25rem;
      font-weight: 600;
    }
  }
}

.sibling-banner {
  background: #e8f2fd;
  border: 1px solid #b6d7f5;
  border-radius: 6px;
  padding: 0.75rem 1rem;
  margin-bottom: 1rem;
  font-size: 0.9rem;
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;

  i {
    margin-top: 0.15rem;
  }
}

.multi-child-hint {
  background: #f0f7f0;
  border: 1px solid #cbe3cb;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin-bottom: 1.25rem;
  font-size: 0.9rem;
  color: #2f5d3a;
  display: flex;
  gap: 0.5rem;
  align-items: flex-start;

  i {
    margin-top: 0.15rem;
    color: #3a8a4f;
  }
}

.email-exists-hint {
  color: #2f5d3a;
  background: #f0f7f0;
  border: 1px solid #cbe3cb;
  border-radius: 6px;
  padding: 0.5rem 0.75rem;

  i {
    color: #3a8a4f;
    margin-right: 0.3rem;
  }
}

.locked-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.25rem;
  margin-left: 0.5rem;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #6c757d;
  background: #eef0f2;
  border-radius: 4px;
  padding: 0.1rem 0.4rem;
}

.locked-input {
  background: #f4f6f8 !important;
  color: #6c757d;
  cursor: not-allowed;
}

.registered-children {
  background: #f0f7f0;
  border: 1px solid #cbe3cb;
  border-radius: 8px;
  padding: 0.75rem 1rem;
  margin: 0.75rem auto;
  max-width: 420px;
  text-align: left;

  &__label {
    font-weight: 600;
    margin: 0 0 0.4rem;
    color: #2f5d3a;
  }

  ul {
    margin: 0;
    padding: 0;
    list-style: none;
  }

  li {
    padding: 0.15rem 0;

    i {
      color: #3a8a4f;
      margin-right: 0.4rem;
    }
  }
}

a.header-wrapper {
  text-decoration: none;
  cursor: pointer;

  header {

    h1,
    p {
      text-decoration: none;
      cursor: pointer;
    }
  }
}

/* Form Intro */
.form__intro {
  margin-bottom: 1.5rem;
  color: #666;
  font-size: 0.95rem;
}

.required-star {
  color: #dc3545;
  font-weight: bold;
}

/* Progress Indicator */
.form__progress {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 2rem;
  padding: 1rem;
  background: #f8f9fa;
  border-radius: 12px;
}

.progress-step {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;

  .step-number {
    width: 32px;
    height: 32px;
    border-radius: 50%;
    background: #dee2e6;
    color: #6c757d;
    display: flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 0.9rem;
    transition: all 0.3s ease;
  }

  .step-label {
    font-size: 0.75rem;
    color: #6c757d;
    font-weight: 500;
  }

  &.active .step-number {
    background: #47a9f9;
    color: white;
  }

  &.completed .step-number {
    background: #28a745;
    color: white;
  }
}

.progress-line {
  flex: 1;
  height: 3px;
  background: #dee2e6;
  margin: 0 0.5rem;
  margin-bottom: 1.5rem;
  max-width: 60px;
  transition: background 0.3s ease;

  &.active {
    background: #28a745;
  }
}

/* Form Sections */
.form__section {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  border: 1px solid #e9ecef;
}

.form__section-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #333;
  margin: 0 0 1.25rem 0;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid #e9ecef;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  i {
    color: #47a9f9;
  }
}

/* Form Row for side-by-side fields */
.form__row {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 1rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
}

/* Input improvements */
.form__input {
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    border-color: #47a9f9;
    box-shadow: 0 0 0 3px rgba(71, 169, 249, 0.15);
  }

  &.success-outline {
    border-color: #28a745;
  }

  &.danger-outline {
    border-color: #dc3545;
  }
}

.form__hint {
  font-size: 0.8rem;
  color: #6c757d;
  margin-top: 0.35rem;
  margin-bottom: 0;
}

/* Password field */
.password-wrap {
  position: relative;

  .form__input {
    padding-right: 45px;
  }
}

.password-toggle {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #6c757d;
  cursor: pointer;
  padding: 0.25rem;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #333;
  }

  i {
    font-size: 1.1rem;
  }
}

.password-requirements {
  margin-top: 0.5rem;
  font-size: 0.8rem;

  span {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    color: #6c757d;

    &.met {
      color: #28a745;
    }

    i {
      font-size: 0.85rem;
    }
  }
}

/* Select styling */
.form__select {
  width: 100%;
  padding: 0.75rem 1rem;
  border: 1px solid #ddd;
  border-radius: 8px;
  background: white;
  font-size: 1rem;
  cursor: pointer;
  transition: border-color 0.2s ease;

  &:focus {
    outline: none;
    border-color: #47a9f9;
  }
}

/* Consent items */
.consent-select-all {
  cursor: pointer;

  .custom-control-label {
    cursor: pointer;
    font-size: 1.1rem;
    font-weight: 600;
  }

  .select-all-hint {
    font-weight: 400;
    font-size: 0.8rem;
    color: #888;
  }
}

.consent-item {
  background: white;
  padding: 1rem;
  border-radius: 8px;
  margin-bottom: 0.75rem;
  border: 1px solid #e9ecef;

  &.gdpr-consent {
    border-left: 4px solid #47a9f9;
  }
}

.consent-text {
  font-size: 0.9rem;
  line-height: 1.5;
  color: #333;

  strong {
    display: block;
    margin-bottom: 0.25rem;
  }
}

.consent-link {
  color: #47a9f9;
  text-decoration: underline;

  &:hover {
    color: darken(#47a9f9, 10%);
  }
}

.is-invalid {
  border-color: #dc3545 !important;
}

/* Actions section */
.form__item--actions {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin-top: 1.5rem;
  padding-top: 1.5rem;
  border-top: 1px solid #e9ecef;
}

.login-link-wrapper {
  text-align: center;
  font-size: 0.95rem;

  span {
    color: #666;
  }

  .form__link {
    margin-left: 0.5rem;
    font-weight: 600;
  }
}

.register-button {
  width: 100%;
  padding: 1rem !important;
  font-size: 1.1rem !important;
  font-weight: 600 !important;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
}

.form__submit-hint {
  text-align: center;
  font-size: 0.85rem;
  color: #6c757d;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.35rem;

  i {
    color: #47a9f9;
  }
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .form__progress {
    padding: 0.75rem;
  }

  .progress-step .step-label {
    display: none;
  }

  .progress-line {
    margin-bottom: 0;
  }

  .form__section {
    padding: 1rem;
  }
}
</style>
